import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, ChevronRight, Info, RefreshCw } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import OfferStrip from '../components/common/OfferStrip';
import SebiStatCards from '../components/sebi-filings/SebiStatCards';
import SebiFilingsTable from '../components/sebi-filings/SebiFilingsTable';
import { fetchSebiFilings } from '../store/slices/sebiFilingsSlice';
import { SEBI_TABS, buildSebiRows, computeSebiStats, filterSebiRows, sebiTabByKey } from '../utils/sebiFilings';

const HEADING = 'SEBI Filings & Approvals';
const DATA_MAX_AGE_MS = 5 * 60 * 1000;

const formatFetchedAt = (timestamp) =>
  timestamp
    ? new Date(timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

/**
 * Every DRHP under SEBI review and every issue that has reached the RHP
 * stage: GET /api/core/ipos/sebi-filings-approvals.
 */
export const SebiFilingsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = sebiTabByKey(searchParams.get('tab')).key;
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const tableRef = useRef(null);

  const items = useSelector((state) => state.sebiFilings.items);
  const totalFiltered = useSelector((state) => state.sebiFilings.totalFiltered);
  const status = useSelector((state) => state.sebiFilings.status);
  const error = useSelector((state) => state.sebiFilings.error);
  const fetchedAt = useSelector((state) => state.sebiFilings.fetchedAt);

  useEffect(() => {
    dispatch(fetchSebiFilings({ maxAgeMs: DATA_MAX_AGE_MS }));
  }, [dispatch]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING}: DRHP Filings Under Review & RHP Approvals | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => setPage(1), [tab, query]);

  const allRows = useMemo(() => buildSebiRows(items), [items]);
  const stats = useMemo(() => computeSebiStats(allRows), [allRows]);
  const tabCounts = useMemo(
    () => Object.fromEntries(SEBI_TABS.map((option) => [option.key, allRows.filter(option.test).length])),
    [allRows]
  );
  const rows = useMemo(() => filterSebiRows(allRows, { tab, query }), [allRows, tab, query]);

  const isLoading = !items.length && (status === 'idle' || status === 'loading');
  const hasFailed = status === 'failed' && !items.length;
  // The endpoint returns at most 200 records and cannot page past them
  const isCapped = totalFiltered > items.length;

  const changeTab = (key) => setSearchParams(key === 'all' ? {} : { tab: key }, { replace: true });

  const changePage = (nextPage) => {
    setPage(nextPage);
    // Paging from the bottom of the table: bring its first row back into view
    if (tableRef.current && tableRef.current.getBoundingClientRect().top < 0) {
      tableRef.current.scrollIntoView({ block: 'start' });
    }
  };

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <OfferStrip slotId={['sebi-filings-top', 'leaderboard']} />

        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pt-5 sm:pt-6 pb-12 sm:pb-16 space-y-5 sm:space-y-6">
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-slate-900 font-semibold" aria-current="page">
                {HEADING}
              </span>
            </nav>

            <div className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight">{HEADING}</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Companies whose draft prospectus (DRHP) is with SEBI, and those that have received SEBI&apos;s
                  observations and filed a red herring prospectus (RHP) — the last step before an issue opens.
                  {formatFetchedAt(fetchedAt) && <span className="text-slate-400"> Updated {formatFetchedAt(fetchedAt)}.</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dispatch(fetchSebiFilings())}
                disabled={status === 'loading'}
                className="self-start lg:self-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60 shadow-sm transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          <SebiStatCards stats={stats} loading={isLoading} />

          <p className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600 leading-relaxed">
            <Info className="w-4 h-4 mt-px text-[#008080] flex-shrink-0" aria-hidden="true" />
            <span>
              <strong className="font-bold text-slate-800">About the dates:</strong> “DRHP Added” and “RHP Recorded” are the
              days each document reached this tracker, not SEBI&apos;s own filing or observation dates. Open a filing for the
              dates stated in the offer document itself.
            </span>
          </p>

          {isCapped && (
            <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <AlertCircle className="w-4 h-4 mt-px flex-shrink-0" aria-hidden="true" />
              Showing the {items.length} most recent of {totalFiltered} filings. Older filings are not available from this list yet.
            </p>
          )}

          {hasFailed ? (
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-8 sm:p-12 text-center">
              <AlertCircle className="w-10 h-10 mx-auto text-rose-400" aria-hidden="true" />
              <h2 className="mt-3 text-lg font-extrabold text-slate-900">SEBI filings could not be loaded</h2>
              <p className="mt-1.5 text-sm text-slate-500">{error}</p>
              <button
                type="button"
                onClick={() => dispatch(fetchSebiFilings())}
                className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try again
              </button>
            </div>
          ) : (
            <SebiFilingsTable
              ref={tableRef}
              rows={rows}
              tab={tab}
              onTabChange={changeTab}
              tabCounts={tabCounts}
              query={query}
              onQueryChange={setQuery}
              page={page}
              onPageChange={changePage}
              isLoading={isLoading}
              emptyMessage={
                query ? (
                  <>
                    No filing matches “{query.trim()}”.{' '}
                    <button type="button" onClick={() => setQuery('')} className="font-bold text-[#008080] hover:underline">
                      Clear search
                    </button>
                  </>
                ) : (
                  'No filings in this stage right now.'
                )
              }
            />
          )}
        </div>
      </main>
    </>
  );
};

export default SebiFilingsPage;
